import type { Cell } from '@/lib/api/models'
import { isIdentifierColumn, isMonetaryColumn } from './columns'

export const EMPTY_CELL = '—'

/**
 * `true` only for values that are unambiguously numeric. Deliberately strict:
 * `Number('')`, `Number(' ')` and `Number('0x1f')` are all finite, and none of
 * them should right-align a column.
 */
export function isNumericValue(value: Cell): boolean {
  if (typeof value === 'number') return Number.isFinite(value)
  if (typeof value !== 'string') return false

  const normalized = value.replace(/,/g, '').trim()
  if (!normalized) return false

  return /^-?\d+(\.\d+)?$/.test(normalized)
}

export function toNumber(value: Cell): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null

  const normalized = value.replace(/,/g, '').trim()
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null

  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

export interface FormatCellOptions {
  column?: string
  locale?: string
}

export function formatCell(
  value: Cell,
  { column = '', locale = 'en-IN' }: FormatCellOptions = {},
): string {
  if (value === null || value === undefined || value === '') return EMPTY_CELL
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'

  const numeric = toNumber(value)

  if (numeric !== null) {
    // Identifiers keep their raw digits — no thousands separators.
    if (isIdentifierColumn(column)) return String(value)

    if (isMonetaryColumn(column)) {
      return numeric.toLocaleString(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    }

    return numeric.toLocaleString(locale, { maximumFractionDigits: 2 })
  }

  return String(value)
}

export function formatCount(value: number, locale = 'en-IN'): string {
  return Number.isFinite(value) ? value.toLocaleString(locale) : '0'
}

export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—'
  if (ms < 1000) return `${Math.round(ms)}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`
}

export function formatTimestamp(value: number, locale = 'en-IN'): string {
  try {
    return new Date(value).toLocaleString(locale, {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return new Date(value).toISOString()
  }
}

export function formatRelativeTime(value: number, now = Date.now()): string {
  const diff = Math.max(0, now - value)
  if (diff < 60_000) return 'just now'
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`
  return `${Math.floor(diff / 86_400_000)}d ago`
}
